import React, { FC, RefObject, useEffect, useRef } from 'react';
import cn from 'classnames';

import { useResponsive, useAppSelector } from 'client/hooks';
import { selectRoomId } from 'client/redux/room/selectors';
import { selectUserRoomRole } from 'client/redux/client/selectors';
import { USER_ROLES } from 'constants/index';

import Icon, { IconType } from '../../Icon';
import DotsLoader from '../../DotsLoader';
import WaveSpeakAnimation from '../../WaveSpeakAnimation';

import css from './ListenerIcon.module.scss';

type Props = {
  name: string;
  onOpen?: () => void;
  pending?: boolean;
  isActive?: boolean;
  isPaused?: boolean;
  isSpeaking?: boolean;
  isAllowedToSpeak?: boolean;
  wantToSpeak?: boolean;
  timesSpoke?: string | number;
  className?: string;
  classNameOfName?: string;
  setRef?: React.Dispatch<React.SetStateAction<RefObject<HTMLDivElement> | undefined>>;
};

const ListenerIcon: FC<Props> = (props) => {
  const {
    name,
    isActive = false,
    isSpeaking = false,
    isAllowedToSpeak = false,
    pending,
    onOpen,
    isPaused,
    wantToSpeak,
    timesSpoke = '0',
    className = '',
    classNameOfName = '',
    setRef,
  } = props;
  const [isMobile] = useResponsive('MOBILE');
  const backgroundRef = useRef<HTMLDivElement>(null);

  const roomId = useAppSelector(selectRoomId);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomId));

  const iconType: IconType = isPaused ? 'pause' : wantToSpeak ? 'hand' : 'none';

  const getInitials = (): string => {
    const initialsArr: string[] = name.trim().split(' ');

    const initials: string = initialsArr.reduce((initials, currentName) => {
      return (initials += currentName[0]);
    }, '');

    return initials;
  };

  const openPopup = (): void => {
    if (onOpen) onOpen();
  };

  useEffect(() => {
    if (backgroundRef.current && setRef) setRef(backgroundRef);
  }, []);

  return (
    <div
      className={cn(
        className,
        css.listener,
        isAllowedToSpeak && css.speaking,
        userRole !== USER_ROLES.LISTENER && css.isCursor,
      )}
    >
      <div className={cn(css.bg, isMobile && isActive && css.isActive)} onClick={openPopup} ref={backgroundRef}>
        {!pending && isSpeaking && <WaveSpeakAnimation className={css.wave} isSpeaking={isSpeaking} />}

        {pending ? <DotsLoader /> : <span>{getInitials()}</span>}

        {(isPaused || wantToSpeak) && (
          <div className={css.handContainer}>
            <Icon type={iconType} />
          </div>
        )}
        {Number(timesSpoke) !== 0 && (
          <div className={css.speakTimes}>
            <span>{timesSpoke}</span>
          </div>
        )}
        <div className={css.activeBorder}>
          <svg width="59" height="58" viewBox="0 0 59 58" fill="none">
            <circle cx="29.5" cy="29" r="28" stroke="black" strokeWidth="2" />
          </svg>
        </div>
      </div>
      <div className={cn(css.name, classNameOfName)}>{name}</div>
    </div>
  );
};

export default ListenerIcon;
